// src/hooks/usePublicPDFAnnotations.ts
// Hook for managing PDF annotations on public share pages (no auth required)
import { useState, useCallback } from 'react';
import { message } from 'antd';
import type {
  Annotation,
  CreateAnnotationRequest,
  UpdateAnnotationRequest,
} from '../types/annotation';

interface UsePublicPDFAnnotationsReturn {
  annotations: Annotation[];
  loading: boolean;
  error: string | null;
  fetchAnnotations: () => Promise<void>;
  createAnnotation: (data: CreateAnnotationRequest) => Promise<Annotation | null>;
  updateAnnotation: (id: string, data: UpdateAnnotationRequest) => Promise<Annotation | null>;
  deleteAnnotation: (id: string) => Promise<boolean>;
  refreshAnnotations: () => Promise<void>;
}

interface UsePublicPDFAnnotationsProps {
  shareToken: string;
  viewerEmail?: string;
  viewerName?: string;
}

export const usePublicPDFAnnotations = ({
  shareToken,
  viewerEmail,
  viewerName,
}: UsePublicPDFAnnotationsProps): UsePublicPDFAnnotationsReturn => {
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

  /**
   * Fetch annotations for the share
   */
  const fetchAnnotations = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      console.log(`[usePublicPDFAnnotations] Fetching annotations for share: ${shareToken}`);

      const response = await fetch(`${API_URL}/share/${shareToken}/annotations`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch annotations');
      }

      const data = await response.json();

      console.log('[usePublicPDFAnnotations] Fetch response:', data);

      // Extract annotations - handle possible response structures
      let fetchedAnnotations: Annotation[] = [];

      if (data.data?.annotations) {
        fetchedAnnotations = data.data.annotations;
      } else if (data.annotations) {
        fetchedAnnotations = data.annotations;
      } else if (Array.isArray(data.data)) {
        fetchedAnnotations = data.data;
      } else if (Array.isArray(data)) {
        fetchedAnnotations = data;
      }

      // Transform snake_case fields to camelCase
      const transformedAnnotations = fetchedAnnotations.map((ann: any) => ({
        id: ann.id || ann.annotation_id,
        documentId: ann.documentId || ann.document_id,
        userId: ann.userId || ann.user_id,
        page: ann.page,
        type: ann.type,
        position: ann.position,
        comment: ann.comment || '',
        tags: ann.tags || [],
        color: ann.color,
        authorName: ann.authorName || ann.author_name || '',
        authorEmail: ann.authorEmail || ann.author_email || '',
        createdAt: ann.createdAt || ann.created_at,
        updatedAt: ann.updatedAt || ann.updated_at,
      }));

      console.log(`[usePublicPDFAnnotations] Loaded ${transformedAnnotations.length} annotations`);

      setAnnotations(transformedAnnotations);
      setLoading(false);
    } catch (err: any) {
      console.error('[usePublicPDFAnnotations] Error fetching annotations:', err);
      setError(err.message || 'Failed to fetch annotations');
      setAnnotations([]);
      setLoading(false);
      message.error(err.message || 'Failed to load annotations');
    }
  }, [API_URL, shareToken]);

  /**
   * Create a new annotation
   */
  const createAnnotation = useCallback(async (
    data: CreateAnnotationRequest
  ): Promise<Annotation | null> => {
    setError(null);

    try {
      console.log('[usePublicPDFAnnotations] Creating annotation:', data);

      // Use viewer info from props
      const authorName = viewerName || viewerEmail || 'Anonymous';
      const authorEmail = viewerEmail || '';

      // Transform camelCase to snake_case for backend
      const requestBody = {
        page: data.page,
        type: data.type,
        position: data.position,
        comment: data.comment || '',
        tags: data.tags || [],
        color: data.color || '#FFEB3B',
        author_name: authorName,
        author_email: authorEmail,
        viewer_email: authorEmail,  // Backend requires viewer_email for public annotations
      };

      console.log('[usePublicPDFAnnotations] Full request body:', requestBody);

      const response = await fetch(`${API_URL}/share/${shareToken}/annotations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create annotation');
      }

      const responseData = await response.json();

      console.log('[usePublicPDFAnnotations] Create response:', responseData);

      // Extract annotation from response
      let createdAnnotation: any;

      if (responseData.data?.annotation) {
        createdAnnotation = responseData.data.annotation;
      } else if (responseData.annotation) {
        createdAnnotation = responseData.annotation;
      } else if (responseData.data?.data?.annotation) {
        createdAnnotation = responseData.data.data.annotation;
      } else {
        throw new Error('Invalid response format');
      }

      // Transform snake_case to camelCase
      const transformedAnnotation: Annotation = {
        id: createdAnnotation.id || createdAnnotation.annotation_id,
        documentId: createdAnnotation.documentId || createdAnnotation.document_id,
        userId: createdAnnotation.userId || createdAnnotation.user_id,
        page: createdAnnotation.page,
        type: createdAnnotation.type,
        position: createdAnnotation.position,
        comment: createdAnnotation.comment || '',
        tags: createdAnnotation.tags || [],
        color: createdAnnotation.color,
        authorName: createdAnnotation.authorName || createdAnnotation.author_name || '',
        authorEmail: createdAnnotation.authorEmail || createdAnnotation.author_email || '',
        createdAt: createdAnnotation.createdAt || createdAnnotation.created_at,
        updatedAt: createdAnnotation.updatedAt || createdAnnotation.updated_at,
      };

      // Add to local state
      setAnnotations((prev) => [...prev, transformedAnnotation]);

      message.success('Comment created');

      return transformedAnnotation;
    } catch (err: any) {
      console.error('[usePublicPDFAnnotations] Error creating annotation:', err);
      setError(err.message || 'Failed to create annotation');
      message.error(err.message || 'Failed to create annotation');
      return null;
    }
  }, [API_URL, shareToken, viewerEmail, viewerName]);

  /**
   * Update an existing annotation
   */
  const updateAnnotation = useCallback(async (
    id: string,
    data: UpdateAnnotationRequest
  ): Promise<Annotation | null> => {
    setError(null);

    try {
      console.log(`[usePublicPDFAnnotations] Updating annotation ${id}:`, data);

      const response = await fetch(`${API_URL}/share/${shareToken}/annotations/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update annotation');
      }

      const responseData = await response.json();

      console.log('[usePublicPDFAnnotations] Update response:', responseData);

      // Extract annotation from response
      let updatedAnnotation: any;

      if (responseData.data?.annotation) {
        updatedAnnotation = responseData.data.annotation;
      } else if (responseData.annotation) {
        updatedAnnotation = responseData.annotation;
      } else if (responseData.data?.data?.annotation) {
        updatedAnnotation = responseData.data.data.annotation;
      } else {
        throw new Error('Invalid response format');
      }

      // Transform snake_case to camelCase
      const transformedAnnotation: Annotation = {
        id: updatedAnnotation.id || updatedAnnotation.annotation_id,
        documentId: updatedAnnotation.documentId || updatedAnnotation.document_id,
        userId: updatedAnnotation.userId || updatedAnnotation.user_id,
        page: updatedAnnotation.page,
        type: updatedAnnotation.type,
        position: updatedAnnotation.position,
        comment: updatedAnnotation.comment || '',
        tags: updatedAnnotation.tags || [],
        color: updatedAnnotation.color,
        authorName: updatedAnnotation.authorName || updatedAnnotation.author_name || '',
        authorEmail: updatedAnnotation.authorEmail || updatedAnnotation.author_email || '',
        createdAt: updatedAnnotation.createdAt || updatedAnnotation.created_at,
        updatedAt: updatedAnnotation.updatedAt || updatedAnnotation.updated_at,
      };

      // Update local state
      setAnnotations((prev) =>
        prev.map((ann) => (ann.id === id ? transformedAnnotation : ann))
      );

      message.success('Annotation updated successfully');

      return transformedAnnotation;
    } catch (err: any) {
      console.error('[usePublicPDFAnnotations] Error updating annotation:', err);
      setError(err.message || 'Failed to update annotation');
      message.error(err.message || 'Failed to update annotation');
      return null;
    }
  }, [API_URL, shareToken]);

  /**
   * Delete an annotation
   */
  const deleteAnnotation = useCallback(async (id: string): Promise<boolean> => {
    setError(null);

    try {
      console.log(`[usePublicPDFAnnotations] Deleting annotation ${id}`);

      // Include viewer_email as query param for ownership verification
      const url = new URL(`${API_URL}/share/${shareToken}/annotations/${id}`);
      if (viewerEmail) {
        url.searchParams.set('viewer_email', viewerEmail);
      }

      const response = await fetch(url.toString(), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete annotation');
      }

      console.log(`[usePublicPDFAnnotations] Annotation ${id} deleted successfully`);

      // Remove from local state
      setAnnotations((prev) => prev.filter((ann) => ann.id !== id));

      message.success('Comment deleted');

      return true;
    } catch (err: any) {
      console.error('[usePublicPDFAnnotations] Error deleting annotation:', err);
      setError(err.message || 'Failed to delete annotation');
      message.error(err.message || 'Failed to delete annotation');
      return false;
    }
  }, [API_URL, shareToken, viewerEmail]);

  /**
   * Refresh annotations
   */
  const refreshAnnotations = useCallback(async () => {
    await fetchAnnotations();
  }, [fetchAnnotations]);

  return {
    annotations,
    loading,
    error,
    fetchAnnotations,
    createAnnotation,
    updateAnnotation,
    deleteAnnotation,
    refreshAnnotations,
  };
};
